import { useInert } from '@orn/shared-web/hooks/useInert';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

import { prefersReducedMotion, SETTLE_TIMEOUT_MARGIN_MS } from '../../hooks/transition/transitionStageDriver';
import { SIDE_PANEL_TRANSITION_DURATION_STYLE, SIDE_PANEL_TRANSITION_MS } from '../../utils/sidePanelMotion';

const TRANSLATE_PROPERTIES = new Set(['translate', 'transform']);

const FOCUSABLE_SELECTOR = 'button:not([disabled]), [href], input, textarea, [tabindex]:not([tabindex="-1"])';

/** The strip of the page left showing above the sheet's rounded corners (`top-3`). */
export const ASSISTANT_SHEET_TOP_INSET_PX = 12;

interface IAssistantSheetProps {
  isOpen: boolean;
  /** The chevron, the scrim, or Escape. */
  onClose: () => void;
  /**
   * Whether Escape closes the sheet. `false` while a layer inside it owns the
   * key — the full Notification Center closes itself on Escape first, and the
   * sheet must not close underneath it on the same press.
   */
  escapeCloses: boolean;
  children: React.ReactNode;
}

/**
 * The bottom sheet the assistant pane rides in below `md`: a `bg-app_light`
 * surface with 24px top corners over a dimmed page, sliding up from the
 * bottom edge (Figma `9155:243072` in Mantle — UX/UI V2).
 *
 * Always mounted, never a modal primitive: the pane inside carries the live
 * WebSocket and the user's draft, so closing hides the sheet (`inert`,
 * translated off-screen) rather than unmounting it, and reopening is instant.
 * Children first mount on the first open, so a page load does not dial the
 * socket for a sheet nobody has asked for.
 *
 * Focus moves into the sheet once the slide-up has ended and back to the
 * opener once the slide-down has — not in a layout effect, where the host's
 * own `inert` toggle on the page would swallow the move. Under reduced motion
 * the same moves happen on the next frame.
 */
const AssistantSheet = ({ isOpen, onClose, escapeCloses, children }: IAssistantSheetProps) => {
  const sheetRef = React.useRef<HTMLDivElement>(null);
  const openerRef = React.useRef<Element | null>(null);
  const pendingFocusRef = React.useRef<'enter' | 'exit' | null>(null);
  const isFirstRenderRef = React.useRef(true);

  useInert(sheetRef, !isOpen);

  // Flipped during render so a sheet that is open on its first frame (a deep
  // link) mounts the pane in that same frame.
  const [hasEverOpened, setHasEverOpened] = React.useState(isOpen);
  if (isOpen && !hasEverOpened) {
    setHasEverOpened(true);
  }

  const settleFocus = React.useCallback(() => {
    const pending = pendingFocusRef.current;
    pendingFocusRef.current = null;
    if (pending === 'enter') {
      // The first focusable outside any inert region: while the full
      // Notification Center covers the pane, the header's close control is
      // inert and a focus call on it is dropped, so the center's own chevron
      // is the one that can take focus.
      const sheet = sheetRef.current;
      const candidates = sheet ? Array.from(sheet.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)) : [];
      candidates.find((el) => !el.closest('[inert]'))?.focus();
    } else if (pending === 'exit') {
      const opener = openerRef.current;
      openerRef.current = null;
      if (opener instanceof HTMLElement && opener.isConnected) {
        opener.focus();
      }
    }
  }, []);

  React.useLayoutEffect(() => {
    const isFirstRender = isFirstRenderRef.current;
    isFirstRenderRef.current = false;

    if (isOpen) {
      openerRef.current = document.activeElement;
      pendingFocusRef.current = 'enter';
    } else if (isFirstRender) {
      return;
    } else {
      pendingFocusRef.current = 'exit';
    }

    // No transition fires for a sheet already in place on its first frame,
    // nor under reduced motion; the timer is the fallback for a transition
    // that never reports its end.
    if (isFirstRender || prefersReducedMotion()) {
      const frame = requestAnimationFrame(settleFocus);
      return () => cancelAnimationFrame(frame);
    }
    const timer = setTimeout(settleFocus, SIDE_PANEL_TRANSITION_MS + SETTLE_TIMEOUT_MARGIN_MS);
    return () => clearTimeout(timer);
  }, [isOpen, settleFocus]);

  const handleTransitionEnd = React.useCallback(
    (event: React.TransitionEvent<HTMLDivElement>) => {
      // Tailwind's `translate-y-*` drives the `translate` property, not `transform`.
      if (event.target === event.currentTarget && TRANSLATE_PROPERTIES.has(event.propertyName)) {
        settleFocus();
      }
    },
    [settleFocus]
  );

  React.useEffect(() => {
    if (!isOpen || !escapeCloses) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !event.defaultPrevented) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, escapeCloses, onClose]);

  return (
    <>
      <div
        aria-hidden="true"
        onClick={onClose}
        style={SIDE_PANEL_TRANSITION_DURATION_STYLE}
        className={cx(
          'bg-overlay/70 fixed inset-0 z-50 transition-opacity ease-in-out motion-reduce:transition-none',
          isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        )}
      />
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label="Assistant"
        onTransitionEnd={handleTransitionEnd}
        style={SIDE_PANEL_TRANSITION_DURATION_STYLE}
        className={cx(
          'bg-app_light shadow-card fixed inset-x-0 bottom-0 top-3 z-50 flex flex-col overflow-hidden rounded-t-[24px]',
          'transition-transform ease-in-out motion-reduce:transition-none',
          isOpen ? 'translate-y-0' : 'pointer-events-none translate-y-full'
        )}
      >
        <div className="flex min-h-0 flex-1 flex-col">{hasEverOpened && children}</div>
      </div>
    </>
  );
};

export default AssistantSheet;
